import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageLoader } from "@/components/common/Loading";
import { StatusBadge } from "@/components/common/StatusBadge";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Building2, Power } from "lucide-react";
import { toast } from "sonner";

export default function PlatformAccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/platform/accounts/${id}`); setData(data); }
    catch (e) { toast.error(apiError(e)); navigate("/super-admin"); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <PageLoader />;
  const { account, organizations, users } = data;

  const toggleStatus = async () => {
    const next = account.status === "active" ? "inactive" : "active";
    setSaving(true);
    try { await api.patch(`/platform/accounts/${id}`, { status: next }); toast.success(`Account ${next}`); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const saveName = async (name) => {
    try { await api.patch(`/platform/accounts/${id}`, { account_name: name }); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <button onClick={() => navigate("/super-admin")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="back-to-accounts">
        <ArrowLeft className="w-4 h-4" /> All accounts
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-bold text-slate-900" data-testid="account-detail-name">{account.account_name}</h1>
            <StatusBadge status={account.status} />
          </div>
          <p className="text-sm text-slate-400 font-mono mt-1">{account.internal_account_id} · {account.time_zone}</p>
        </div>
        <Button variant="outline" onClick={toggleStatus} disabled={saving} data-testid="toggle-account-status"
          className={account.status === "active" ? "text-rose-600 border-rose-200 hover:bg-rose-50" : "text-emerald-600 border-emerald-200 hover:bg-emerald-50"}>
          <Power className="w-4 h-4 mr-1.5" /> {account.status === "active" ? "Deactivate" : "Activate"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="font-display font-semibold text-slate-900 text-sm mb-4">Account Profile</h3>
          <Label className="text-xs font-semibold text-slate-700">Account name</Label>
          <Input defaultValue={account.account_name} onBlur={(e) => saveName(e.target.value)} className="mt-1 mb-3" data-testid="detail-account-name" />
          <div className="text-sm text-slate-500 space-y-1">
            <p>Primary contact: <span className="text-slate-700">{account.primary_contact?.name || "—"}</span></p>
            <p>Contact email: <span className="text-slate-700">{account.primary_contact?.email || "—"}</span></p>
          </div>
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Feature Settings</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(account.feature_settings || {}).map(([k, v]) => (
                <span key={k} className={`text-xs rounded-full border px-2 py-0.5 ${v ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{k}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="font-display font-semibold text-slate-900 text-sm mb-4">Organizations ({organizations.length})</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto tr-scroll">
            {organizations.map((o) => (
              <div key={o.id} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2">
                <span className="flex items-center gap-2 text-sm text-slate-700"><Building2 className="w-4 h-4 text-slate-400" />{o.name}</span>
                <span className="font-mono text-xs text-slate-500">{o.org_code}</span>
              </div>
            ))}
            {!organizations.length && <p className="text-sm text-slate-400">No organizations.</p>}
          </div>
        </div>

        <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="font-display font-semibold text-slate-900 text-sm mb-4">Users ({users.length})</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 rounded-md border border-slate-100 px-3 py-2">
                <UserAvatar name={u.name} size={30} />
                <div className="min-w-0"><p className="text-sm font-medium text-slate-700 truncate">{u.name}</p>
                  <p className="text-xs text-slate-400 truncate">{(u.roles || []).join(", ")}</p></div>
                <div className="ml-auto"><StatusBadge status={u.status} /></div>
              </div>
            ))}
            {!users.length && <p className="text-sm text-slate-400">No users.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
